import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  ButtonBase,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { AddItemMenu } from '../../../../components/Billing/BillingLinesEditor';
import DiscountRow from '../../../../components/Billing/DiscountRow';
import { LINE_COLUMNS, LineTableHeader, lineFieldSx, PriceField, VatDialog } from '../../../../components/Billing/LineTable';
import GenericButton, { GenericButtonTypeEnum } from '../../../../components/GenericButton';
import GenericInfoModal from '../../../../components/GenericInfoModal';
import RowActionsMenu from '../../../../components/RowActionsMenu/RowActionsMenu';
import { BillingItemTypeEnum, DiscountModeEnum, INVOICE_LINE_TYPE_LABELS, InvoiceLineTypeEnum } from '../../../../common/enums/invoicing.enum';
import { BillingItemDto, InvoiceLineDto, InvoiceLineInputDto, InvoicePreviewLineDto } from '../../../../common/interfaces/invoicing.interface';
import { calculateLineTotals, formatMoney, LineTotals, sumLineTotals } from '../../../../utilities/helper/money.helper';
import { discountAmounts, DiscountProblem, discountProblems, parseDiscountValue } from '../../../../utilities/helper/discount.helper';

/**
 * A line being edited; `key` is the editor's own and is never sent. A discount is a line
 * too, with its value as typed and the key of the line it lowers.
 */
export interface InvoiceEditorLine extends InvoiceLineInputDto {
  key: string;
  /** DISCOUNT lines only. */
  discountValueText?: string;
  appliesToKey?: string;
}

const EDITABLE_TYPES = [InvoiceLineTypeEnum.FIXED_QUANTITY, InvoiceLineTypeEnum.ATTENDANCE_BASED, InvoiceLineTypeEnum.MANUAL];

let nextKey = 0;
const newKey = () => `line-${++nextKey}`;

const isDiscount = (line: { lineType: InvoiceLineTypeEnum }) => line.lineType === InvoiceLineTypeEnum.DISCOUNT;

/**
 * An attendance-based line gets its quantity from recorded attendance on the server, so it
 * only has one here once the draft has been saved and read back.
 */
export const hasKnownQuantity = (line: InvoiceLineInputDto): boolean =>
  line.lineType !== InvoiceLineTypeEnum.ATTENDANCE_BASED || typeof line.quantity === 'number';

/** Mirrors the backend rounding - once per line, then summed - so totals do not jump on save. */
const ownTotals = (line: InvoiceLineInputDto, vatApplicable: boolean): LineTotals =>
  hasKnownQuantity(line)
    ? calculateLineTotals(line.unitPriceNet ?? 0, line.quantity ?? 0, vatApplicable ? (line.vatRate ?? 0) : 0, line.discountPercent ?? 0)
    : { net: 0, vat: 0, gross: 0 };

/** What each discount takes off, by its key: null while its line's amount is not known. */
const discountTakes = (lines: InvoiceEditorLine[]): Record<string, number | null> => {
  const result: Record<string, number | null> = {};
  lines.filter(line => !isDiscount(line)).forEach(target => {
    const own = lines.filter(line => isDiscount(line) && line.appliesToKey === target.key);
    if (!hasKnownQuantity(target)) {
      own.forEach(discount => (result[discount.key] = null));
      return;
    }
    const net = calculateLineTotals(target.unitPriceNet ?? 0, target.quantity ?? 0, 0, target.discountPercent ?? 0).net;
    const amounts = discountAmounts(
      net,
      own.map(discount => ({ mode: discount.discountMode ?? DiscountModeEnum.PERCENT, value: parseDiscountValue(discount.discountValueText ?? '') }))
    );
    own.forEach((discount, index) => (result[discount.key] = amounts[index]));
  });
  return result;
};

/**
 * Totals the way the backend works them out: every line rounded once, a discount at its
 * line's VAT rate and never more than the line, then summed.
 */
export const editorTotals = (
  lines: InvoiceEditorLine[],
  vatApplicable: boolean
): { monthly: number; oneOff: number; discounts: number; total: number; pendingAttendance: boolean } => {
  const takes = discountTakes(lines);
  const sum = (types: InvoiceLineTypeEnum[]) =>
    sumLineTotals(lines.filter(line => types.includes(line.lineType)).map(line => ownTotals(line, vatApplicable))).gross;
  const discounts = sumLineTotals(
    lines.filter(isDiscount).map(discount => {
      const target = lines.find(line => line.key === discount.appliesToKey);
      return calculateLineTotals(takes[discount.key] ?? 0, 1, vatApplicable ? (target?.vatRate ?? 0) : 0);
    })
  ).gross;
  const monthly = sum([InvoiceLineTypeEnum.FIXED_QUANTITY, InvoiceLineTypeEnum.ATTENDANCE_BASED]);
  const oneOff = sum([InvoiceLineTypeEnum.MANUAL]);
  return {
    monthly,
    oneOff,
    discounts,
    total: Math.round((monthly + oneOff - discounts) * 100) / 100,
    pendingAttendance: lines.some(line => !isDiscount(line) && !hasKnownQuantity(line))
  };
};

/** Every discount's problems by key; empty when the lines can be saved. */
export const invoiceDiscountProblems = (t: TFunction, lines: InvoiceEditorLine[]): Record<string, DiscountProblem> => {
  const result: Record<string, DiscountProblem> = {};
  lines.filter(line => !isDiscount(line)).forEach(target => {
    const own = lines.filter(line => isDiscount(line) && line.appliesToKey === target.key);
    const net = hasKnownQuantity(target) ? calculateLineTotals(target.unitPriceNet ?? 0, target.quantity ?? 0).net : null;
    const problems = discountProblems(
      t as any,
      net,
      own.map(discount => ({ label: discount.description, mode: discount.discountMode ?? DiscountModeEnum.PERCENT, valueText: discount.discountValueText ?? '' }))
    );
    own.forEach((discount, index) => {
      if (problems[index].label || problems[index].value) result[discount.key] = problems[index];
    });
  });
  return result;
};

const valueText = (value: number | null | undefined) => (value === null || value === undefined ? '' : String(value).replace('.', ','));

/** The scheme's lines as the editor holds them; an attendance quantity is the server's to count. */
export const fromPreviewLines = (lines: InvoicePreviewLineDto[]): InvoiceEditorLine[] => {
  const keys = lines.map(() => newKey());
  return lines.map((line, index) => ({
    key: keys[index],
    lineType: line.lineType,
    description: line.description,
    internalName: line.internalName ?? undefined,
    unit: line.unit ?? '',
    quantity: line.lineType === InvoiceLineTypeEnum.ATTENDANCE_BASED ? undefined : line.quantity,
    unitPriceNet: isDiscount(line) ? 0 : line.unitPriceNet,
    vatRate: line.vatRate,
    discountPercent: line.discountPercent,
    sortOrder: line.sortOrder,
    ...(isDiscount(line)
      ? {
          discountMode: line.discountMode ?? DiscountModeEnum.PERCENT,
          discountValueText: valueText(line.discountValue),
          appliesToKey: line.appliesToIndex === null ? undefined : keys[line.appliesToIndex]
        }
      : {})
  }));
};

/** A saved draft's lines; their attendance days are already counted, so they keep them. */
export const toEditorLines = (lines: InvoiceLineDto[]): InvoiceEditorLine[] => {
  const keyOf = new Map(lines.map(line => [line.id, newKey()]));
  return lines.map(line => ({
    key: keyOf.get(line.id) as string,
    lineType: line.lineType,
    description: line.description,
    internalName: line.internalName ?? undefined,
    unit: line.unit ?? '',
    quantity: line.quantity,
    unitPriceNet: isDiscount(line) ? 0 : line.unitPriceNet,
    vatRate: line.vatRate,
    discountPercent: line.discountPercent,
    sortOrder: line.sortOrder,
    ...(isDiscount(line)
      ? {
          discountMode: line.discountMode ?? DiscountModeEnum.PERCENT,
          discountValueText: valueText(line.discountValue),
          appliesToKey: line.appliesToLineId ? keyOf.get(line.appliesToLineId) : undefined
        }
      : {})
  }));
};

/** What is sent: no editor keys, a discount naming its line by position, no attendance quantity. */
export const toLineInputs = (lines: InvoiceEditorLine[]): InvoiceLineInputDto[] =>
  lines.map(({ key, discountValueText, appliesToKey, ...line }, index) => ({
    ...line,
    quantity: line.lineType === InvoiceLineTypeEnum.ATTENDANCE_BASED ? undefined : line.quantity,
    sortOrder: index,
    ...(line.lineType === InvoiceLineTypeEnum.DISCOUNT
      ? {
          unitPriceNet: 0,
          discountValue: parseDiscountValue(discountValueText ?? '') ?? undefined,
          appliesToIndex: lines.findIndex(other => other.key === appliesToKey)
        }
      : {})
  }));

/**
 * Whether the lines still say what the price list says. Drives "restore the price list's
 * prices", which is offered only when there is something to restore.
 */
export const matchesPreview = (lines: InvoiceEditorLine[], preview: InvoicePreviewLineDto[]): boolean =>
  lines.length === preview.length &&
  lines.every((line, i) => {
    const other = preview[i];
    if (line.lineType !== other.lineType || line.description !== other.description) return false;
    if (isDiscount(line)) {
      return (
        line.discountMode === other.discountMode &&
        parseDiscountValue(line.discountValueText ?? '') === other.discountValue &&
        lines.findIndex(target => target.key === line.appliesToKey) === other.appliesToIndex
      );
    }
    return (
      line.unitPriceNet === other.unitPriceNet &&
      (line.vatRate ?? 0) === other.vatRate &&
      (line.lineType === InvoiceLineTypeEnum.ATTENDANCE_BASED || line.quantity === other.quantity)
    );
  });

const fromCatalog = (item: BillingItemDto): InvoiceEditorLine => ({
  key: newKey(),
  lineType: item.type === BillingItemTypeEnum.ATTENDANCE_BASED ? InvoiceLineTypeEnum.ATTENDANCE_BASED : InvoiceLineTypeEnum.FIXED_QUANTITY,
  description: item.pdfLabel,
  internalName: item.name,
  unit: item.unit ?? '',
  quantity: item.type === BillingItemTypeEnum.ATTENDANCE_BASED ? undefined : item.defaultQuantity,
  unitPriceNet: item.unitPriceNet,
  vatRate: item.vatRate
});

const manualLine = (): InvoiceEditorLine => ({
  key: newKey(),
  lineType: InvoiceLineTypeEnum.MANUAL,
  description: '',
  unit: '',
  quantity: 1,
  unitPriceNet: 0,
  vatRate: 0
});

const discountLine = (appliesToKey: string): InvoiceEditorLine => ({
  key: newKey(),
  lineType: InvoiceLineTypeEnum.DISCOUNT,
  description: '',
  unitPriceNet: 0,
  discountMode: DiscountModeEnum.PERCENT,
  discountValueText: '',
  appliesToKey
});

const typeHint = (t: TFunction, type: InvoiceLineTypeEnum): string =>
  type === InvoiceLineTypeEnum.ATTENDANCE_BASED
    ? t('billing.lines.perAttendanceDay')
    : type === InvoiceLineTypeEnum.MANUAL
      ? t('invoices.lines.oneOff')
      : t('billing.lines.monthlyUnit');

const nameOf = (t: TFunction, line: InvoiceEditorLine) => line.internalName || line.description || t('invoices.lines.unnamed');

/** "Name on invoice" for an invoice line: also its unit and how it is counted. */
const LineDialog: React.FC<{ line: InvoiceEditorLine; onClose: () => void; onSave: (patch: Partial<InvoiceEditorLine>) => void }> = ({
  line,
  onClose,
  onSave
}) => {
  const { t } = useTranslation();
  const [description, setDescription] = useState(line.description);
  const [unit, setUnit] = useState(line.unit ?? '');
  const [lineType, setLineType] = useState(line.lineType);
  return (
    <Dialog open onClose={onClose} fullWidth slotProps={{ paper: { sx: { maxWidth: 480, borderRadius: 4 } } }}>
      <DialogTitle sx={{ fontSize: 18, fontWeight: 600 }}>{t('billing.lines.labelDialogTitle', { name: nameOf(t, line) })}</DialogTitle>
      <DialogContent sx={{ display: 'grid', gap: 2, pt: '8px !important' }}>
        <Box>
          <Typography component="label" htmlFor="invoice-line-label" sx={{ display: 'block', fontSize: 13, color: 'text.secondary', mb: 0.75 }}>
            {t('billing.lines.invoiceLabel')}
          </Typography>
          <TextField id="invoice-line-label" fullWidth autoFocus value={description} onChange={event => setDescription(event.target.value)} sx={lineFieldSx} />
        </Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
          <Box>
            <Typography component="label" htmlFor="invoice-line-unit" sx={{ display: 'block', fontSize: 13, color: 'text.secondary', mb: 0.75 }}>
              {t('billing.items.unit')}
            </Typography>
            <TextField id="invoice-line-unit" fullWidth value={unit} onChange={event => setUnit(event.target.value)} sx={lineFieldSx} />
          </Box>
          <Box>
            <Typography id="invoice-line-type-label" sx={{ fontSize: 13, color: 'text.secondary', mb: 0.75 }}>
              {t('invoices.page.type')}
            </Typography>
            <Select
              fullWidth
              labelId="invoice-line-type-label"
              value={lineType}
              onChange={event => setLineType(event.target.value as InvoiceLineTypeEnum)}
              sx={{ height: 44, borderRadius: '10px' }}
            >
              {EDITABLE_TYPES.map(type => (
                <MenuItem key={type} value={type}>
                  {t(INVOICE_LINE_TYPE_LABELS[type])}
                </MenuItem>
              ))}
            </Select>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <GenericButton buttonType={GenericButtonTypeEnum.Neutral} variant="outlined" label={t('common.cancel')} onClick={onClose} />
        <GenericButton
          buttonType={GenericButtonTypeEnum.Primary}
          variant="contained"
          label={t('common.save')}
          onClick={() => {
            onSave({
              description,
              unit,
              lineType,
              quantity: lineType === InvoiceLineTypeEnum.ATTENDANCE_BASED ? undefined : (line.quantity ?? 1)
            });
            onClose();
          }}
        />
      </DialogActions>
    </Dialog>
  );
};

interface InvoiceLinesEditorProps {
  lines: InvoiceEditorLine[];
  onChange: (lines: InvoiceEditorLine[]) => void;
  catalog: BillingItemDto[];
  vatApplicable: boolean;
  /** After a save attempt, every discount problem shows, touched or not. */
  showErrors?: boolean;
}

/**
 * The lines of one invoice, in the rows of the price list's editor: item, price, quantity,
 * total, and the rest in the line's menu.
 *
 * Unlike a price list, an invoice line is a copy and not a reference: changing it here
 * changes this invoice only. A manual line has no name until the admin types one, so its
 * name is a field in the row. A discount sits right under the line it lowers; one that
 * came from the child's contract arrives on its own, one added here is for this invoice.
 */
const InvoiceLinesEditor: React.FC<InvoiceLinesEditorProps> = ({ lines, onChange, catalog, vatApplicable, showErrors }) => {
  const { t } = useTranslation();
  const discountLabels = useRef(new Map<string, HTMLInputElement>());
  const [focusDiscount, setFocusDiscount] = useState<string | null>(null);
  const [editing, setEditing] = useState<InvoiceEditorLine | null>(null);
  const [vatFor, setVatFor] = useState<InvoiceEditorLine | null>(null);
  const [removing, setRemoving] = useState<InvoiceEditorLine | null>(null);

  const setLine = (key: string, patch: Partial<InvoiceEditorLine>) => onChange(lines.map(line => (line.key === key ? { ...line, ...patch } : line)));
  const removeLine = (target: InvoiceEditorLine) => onChange(lines.filter(line => line.key !== target.key && line.appliesToKey !== target.key));

  const addDiscount = (target?: InvoiceEditorLine) => {
    const on = target ?? lines.find(line => line.lineType === InvoiceLineTypeEnum.FIXED_QUANTITY) ?? lines.find(line => !isDiscount(line));
    if (!on) return;
    const discount = discountLine(on.key);
    onChange([...lines, discount]);
    setFocusDiscount(discount.key);
  };

  useEffect(() => {
    if (!focusDiscount) return;
    discountLabels.current.get(focusDiscount)?.focus();
    setFocusDiscount(null);
  }, [focusDiscount, lines]);

  const takes = discountTakes(lines);
  const problems = invoiceDiscountProblems(t, lines);
  const items = lines.filter(line => !isDiscount(line));
  const targets = items.map(line => ({ key: line.key, name: nameOf(t, line) }));
  const available = [...catalog].sort((a, b) => a.name.localeCompare(b.name));

  return (
    <Box>
      {!items.length ? (
        <Typography sx={{ fontSize: 14, color: 'text.secondary' }}>{t('invoices.lines.empty')}</Typography>
      ) : (
        <Box sx={{ border: 1, borderColor: 'line', borderRadius: '10px', overflow: 'hidden' }}>
          <LineTableHeader />
          <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0 }}>
            {items.map(line => {
              const isAttendance = line.lineType === InvoiceLineTypeEnum.ATTENDANCE_BASED;
              const isManual = line.lineType === InvoiceLineTypeEnum.MANUAL;
              const name = nameOf(t, line);
              const own = lines.filter(other => isDiscount(other) && other.appliesToKey === line.key);
              const known = hasKnownQuantity(line);
              return (
                <React.Fragment key={line.key}>
                  <Box
                    component="li"
                    aria-label={name}
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: LINE_COLUMNS,
                      columnGap: 1.5,
                      alignItems: 'start',
                      p: '12px 16px',
                      '& + &': { borderTop: 1, borderColor: 'line' }
                    }}
                  >
                    <Box sx={{ minWidth: 0, pt: isManual ? 0 : 0.5 }}>
                      {isManual ? (
                        <>
                          <TextField
                            fullWidth
                            value={line.description}
                            placeholder={t('invoices.lines.manualPlaceholder')}
                            error={!!showErrors && !line.description.trim()}
                            onChange={event => setLine(line.key, { description: event.target.value })}
                            slotProps={{ htmlInput: { 'aria-label': t('invoices.lines.manualName') } }}
                            sx={lineFieldSx}
                          />
                          <Typography sx={{ fontSize: 12, color: 'text.secondary', mt: 0.5 }}>{typeHint(t, line.lineType)}</Typography>
                        </>
                      ) : (
                        <>
                          <Typography sx={{ fontSize: 15, fontWeight: 500 }}>{name}</Typography>
                          <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                            {[
                              line.description && line.description !== name ? t('billing.lines.onInvoice', { label: line.description }) : null,
                              typeHint(t, line.lineType),
                              line.unit || null
                            ]
                              .filter(Boolean)
                              .join(' · ')}
                          </Typography>
                        </>
                      )}
                    </Box>

                    <PriceField
                      value={line.unitPriceNet}
                      ariaLabel={t('billing.lines.priceFor', { name })}
                      onChange={value => setLine(line.key, { unitPriceNet: value })}
                    />

                    {isAttendance ? (
                      <Typography sx={{ fontSize: 13, color: 'text.secondary', lineHeight: '44px', textAlign: 'right' }}>
                        {known ? t('invoices.lines.daysCount', { count: line.quantity }) : t('billing.lines.perAttendanceDayShort')}
                      </Typography>
                    ) : (
                      <TextField
                        fullWidth
                        type="number"
                        value={line.quantity ?? 1}
                        onChange={event => setLine(line.key, { quantity: Number(event.target.value) })}
                        slotProps={{
                          htmlInput: {
                            min: 0,
                            step: 'any',
                            'aria-label': t('billing.lines.quantityFor', { name }),
                            style: { textAlign: 'right', fontVariantNumeric: 'tabular-nums' }
                          }
                        }}
                        sx={lineFieldSx}
                      />
                    )}

                    <Typography sx={{ fontSize: 15, fontWeight: 600, textAlign: 'right', lineHeight: '44px', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                      {known ? formatMoney(ownTotals(line, vatApplicable).gross) : t('billing.lines.perDayAmount', { amount: formatMoney(line.unitPriceNet) })}
                    </Typography>

                    <Box sx={{ pt: 0.5 }}>
                      <RowActionsMenu
                        name={name}
                        ariaLabel={t('billing.lines.moreFor', { name })}
                        actions={[
                          { label: t('billing.lines.invoiceLabel'), onClick: () => setEditing(line) },
                          { label: t('billing.lines.vatRate'), hidden: !vatApplicable, onClick: () => setVatFor(line) },
                          { label: t('discounts.addToLine'), onClick: () => addDiscount(line) },
                          { label: t('billing.lines.remove'), danger: true, onClick: () => (own.length ? setRemoving(line) : removeLine(line)) }
                        ]}
                      />
                    </Box>
                  </Box>
                  {own.map(discount => (
                    <DiscountRow
                      key={discount.key}
                      discount={{
                        key: discount.key,
                        label: discount.description,
                        mode: discount.discountMode ?? DiscountModeEnum.PERCENT,
                        valueText: discount.discountValueText ?? '',
                        appliesTo: discount.appliesToKey ?? line.key
                      }}
                      onChange={patch =>
                        setLine(discount.key, {
                          ...(patch.label !== undefined ? { description: patch.label } : {}),
                          ...(patch.mode !== undefined ? { discountMode: patch.mode } : {}),
                          ...(patch.valueText !== undefined ? { discountValueText: patch.valueText } : {}),
                          ...(patch.appliesTo !== undefined ? { appliesToKey: patch.appliesTo } : {})
                        })
                      }
                      onRemove={() => onChange(lines.filter(other => other.key !== discount.key))}
                      targets={targets}
                      targetNet={known ? calculateLineTotals(line.unitPriceNet ?? 0, line.quantity ?? 0).net : null}
                      targetName={name}
                      amount={takes[discount.key] ?? null}
                      problem={problems[discount.key]}
                      showErrors={showErrors}
                      labelRef={element => {
                        if (element) discountLabels.current.set(discount.key, element);
                        else discountLabels.current.delete(discount.key);
                      }}
                    />
                  ))}
                </React.Fragment>
              );
            })}
          </Box>
        </Box>
      )}

      <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 1 }}>
        <AddItemMenu available={available} onPick={item => onChange([...lines, fromCatalog(item)])} label={t('invoices.lines.addFromCatalog')} />
        <ButtonBase
          onClick={() => onChange([...lines, manualLine()])}
          sx={{ height: 44, px: 1.5, gap: 0.75, borderRadius: '10px', fontSize: 16, color: 'primary.main', '&:hover': { bgcolor: 'surface.tint' } }}
        >
          <AddIcon sx={{ fontSize: 20 }} />
          {t('invoices.lines.addManual')}
        </ButtonBase>
        {items.length > 0 && (
          <ButtonBase
            onClick={() => addDiscount()}
            sx={{ height: 44, px: 1.5, gap: 0.75, borderRadius: '10px', fontSize: 16, color: 'primary.main', '&:hover': { bgcolor: 'surface.tint' } }}
          >
            <RemoveIcon sx={{ fontSize: 20 }} />
            {t('discounts.add')}
          </ButtonBase>
        )}
      </Stack>

      {editing && <LineDialog line={editing} onClose={() => setEditing(null)} onSave={patch => setLine(editing.key, patch)} />}
      {vatFor && (
        <VatDialog
          title={t('billing.lines.vatDialogTitle', { name: nameOf(t, vatFor) })}
          initial={vatFor.vatRate ?? 0}
          onClose={() => setVatFor(null)}
          onSave={rate => setLine(vatFor.key, { vatRate: rate })}
        />
      )}
      <GenericInfoModal
        open={!!removing}
        onClose={() => setRemoving(null)}
        onConfirm={() => {
          if (removing) removeLine(removing);
          setRemoving(null);
        }}
        label={t('billing.lines.removeTitle', { name: removing ? nameOf(t, removing) : '' })}
        message={
          removing
            ? t('discounts.removeWithLine', {
                item: nameOf(t, removing),
                discounts: lines
                  .filter(line => isDiscount(line) && line.appliesToKey === removing.key)
                  .map(line => line.description.trim() || t('discounts.unnamed'))
                  .join(', ')
              })
            : ''
        }
        confirmLabel={t('billing.lines.remove')}
      />
    </Box>
  );
};

export default InvoiceLinesEditor;
